import type { CaseCopy } from "./types";

export const bpi2017: CaseCopy = {
  slug: "bpi-2017",
  displayName: "BPI Challenge 2017 — Dutch Consumer Loans",
  issueNumberRoman: "I",
  issueNumberArabic: "01",
  mastheadIssue: "Issue I · Apr 2026",

  noteTagline: <>What follows is one such reconstruction, applied to a real bank.</>,

  sourceCard: (
    <>
      The data comes from the <b>BPI Challenge 2017</b>, a yearly benchmark published by the
      4TU research consortium. A Dutch bank released an anonymized log of{" "}
      <b>31,509 consumer-loan applications</b> — 1.2&#8239;million events across the lifecycle
      from apply to offer to cancel or accept. The dataset is public, widely cited, and designed
      so analysts can practice reconstructing real-world workflows. No customer data is
      identifiable; every case is a timestamped trace through the bank's internal systems.
    </>
  ),

  hero: {
    kicker: "Issue I  ·  The Bank That Waited",
    title: (
      <>
        A Dutch bank processed <span className="num-amber">31,509</span> loan applications in a year.{" "}
        <span className="frac">One in three</span> never closed.
      </>
    ),
    dek: (
      <>
        Not because the customers disappeared. Because the bank did — trapped in a machine that
        called, waited, called again, and twenty-seven days later quietly marked the application
        as cancelled. This is what the machine's own logs told us, when we finally asked.
      </>
    ),
  },

  dotGrid: {
    total: 540,
    bad: 147,
    caption: (
      <>
        <b>1 dot = ~58 applications</b>
        540 dots represent all 31,509 applications in the year.{" "}
        <em>147 dots in red</em> are the 8,539 silent cancellations — a single bureaucratic
        pattern, visible at a glance.
      </>
    ),
  },

  lede: (
    <>
      The story the bank told itself was that a loan application flows through five clean steps:
      a customer applies, completes the file, receives an offer, the bank validates, and a
      decision is made. The story the logs tell is messier. Some cases loop. Some wait. Some
      vanish into a quiet drawer the bank calls <em>cancellation</em> — but which is often just
      the name given to a conversation that never finished.
    </>
  ),

  headlineFinding: {
    label: "· 01 · Headline finding",
    h2: "The bank is losing time, not losing customers.",
    body: (
      <>
        The Dutch lender's consumer lending process is not inefficient by design; it is
        inefficient by default. Half of the measured slowness is a single pattern: after sending
        a loan offer, the bank calls the customer, receives silence, and then waits an average of
        twenty-seven days before formally cancelling the application. Compounded across{" "}
        <b>8,539 such cases</b>, this single bureaucratic reflex explains the majority of the p95
        delay.
      </>
    ),
    issues: [
      {
        tag: "Issue I · Silent Cancellation",
        big: "27",
        unit: "d",
        ev: "Average wait between the last unanswered call and formal cancellation — on 8,539 applications this year.",
      },
      {
        tag: "Issue II · Rework",
        big: "115",
        unit: "k",
        ev: "Times that 'validate application' is followed immediately by itself — the same file re-opened, re-checked, re-filed.",
      },
      {
        tag: "Issue III · Tail Risk",
        big: "286",
        unit: "d",
        ev: "Longest single case — nine months from apply to close. p95 is 42.5 days, versus a 21.9-day mean.",
      },
    ],
  },

  sectionTwo: {
    label: "· 02 · The five-step story vs. the actual log",
    diagram: {
      exhibitTag: (<>Exhibit <b>· As-declared five-step process</b> — with reality overlaid in amber</>),
      sourceTag: "Source: DFG over 1.2M events",
      steps: [
        { num: "STEP 01", name: "Apply",    subActs: ["A_Create", "A_Submitted"],          band: "teal"   },
        { num: "STEP 02", name: "Complete", subActs: ["A_Concept", "A_Accepted"],          band: "muted"  },
        { num: "STEP 03", name: "Offer",    subActs: ["O_Create Offer", "O_Sent"],         band: "signal" },
        { num: "STEP 04", name: "Validate", subActs: ["W_Validate app.", "W_Call after offer"], band: "alert"  },
        { num: "STEP 05", name: "Decide",   subActs: ["A_Pending", "A_Denied"],            band: "ink"    },
      ],
      gapDays: ["0.1 d", "6.0 d", "1.2 d", "0.6 d"],
      selfLoops: [{ fromStep: 3, label: "self-loop" }, { fromStep: 4, label: "self-loop" }],
      topNotes: [
        { text: (<><tspan x="274" dy="0">six days here —</tspan><tspan x="274" dy="18">waiting for document return</tspan></>), fromStep: 2, tickColor: "muted", width: 220, x: 260, y: 70 },
        { text: (<><tspan className="n">115,569</tspan> calls on the same file</>), fromStep: 3, tickColor: "signal", width: 220, x: 540, y: 86 },
        { text: (<><tspan x="814" dy="0"><tspan className="n">115,590</tspan> re-validations —</tspan><tspan x="814" dy="18">the same file, re-opened</tspan></>), fromStep: 4, tickColor: "alert", width: 240, x: 800, y: 144 },
      ],
      cancelStub: {
        label: "A_Cancelled · 8,539",
        fromStep: 4,
        note: (<><tspan className="n">8,539</tspan> quiet exits — customer never heard back</>),
      },
      declaredMedianDays: "7.9 d",
      legend: [
        { band: "teal",   label: "Apply · healthy start" },
        { band: "muted",  label: "Complete · neutral" },
        { band: "signal", label: "Offer · hot zone" },
        { band: "alert",  label: "Validate · danger" },
        { band: "ink",    label: "Decide · outcome" },
      ],
    },
    sparkline: {
      title: "Sojourn time — STEP 04 → outcome",
      body: (
        <>
          Most cases resolve quickly; a long, thin tail carries the damage. The{" "}
          <em>median cancellation</em> happens after a long silence — not after a decision.
        </>
      ),
    },
  },

  sectionThree: {
    labelFromCount: (n) => `· 03 · ${toWord(n)} findings from the log`,
  },

  sectionFive: {
    label: "· 05 · As a consultant, I'd recommend",
    h2: "Five moves. No new hires, no new software.",
    pull: "If the bank touches all five, the baseline 21.9 days drops to an estimated ~14 days.",
    recs: [
      { tag: "R1", act: "Auto-cancel stale offers at day 14, not day 27.",                      impact: (<><b>−13 d</b> on 8,539 cases</>) },
      { tag: "R2", act: "Front-load validation — reject incomplete files at intake.",            impact: (<><b>−115,590</b> rework events / yr</>) },
      { tag: "R3", act: "Cap callback attempts at 3 before auto-escalating to email + countdown.", impact: (<><b>kills</b> callback treadmill</>) },
      { tag: "R4", act: "Deprecate physical mail for small-ticket offers.",                      impact: (<><b>−3 d</b> stall, digital channel</>) },
      { tag: "R5", act: "Publish a 45-day hard ceiling. Anything over it pages a supervisor daily.", impact: (<><b>breaks</b> the long tail</>) },
    ],
    close: (
      <>
        If the bank touches all five, the baseline <b>21.9 days</b> drops to an estimated{" "}
        <b>~14 days</b> — no new hires, no new software.
      </>
    ),
  },
};

function toWord(n: number): string {
  const words = ["zero","one","two","three","four","five","six","seven","eight","nine","ten","eleven","twelve"];
  return words[n] ?? String(n);
}
